import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, FileText, CreditCard, MapPin, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { promociones, blogPosts } from '../utils/mockData';

const Home = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 to-blue-100 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="space-y-6">
              <h1 className="text-4xl md:text-5xl font-bold text-[#1e3a8a] leading-tight">
                Cuando buscas certeza, estamos contigo.
              </h1>
              <p className="text-xl text-gray-700">
                No es una muestra, es familia.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/estudios">
                  <Button className="bg-[#84cc16] hover:bg-[#65a30d] text-white px-8 py-6 text-lg rounded-full">
                    Ver estudios
                  </Button>
                </Link>
                <Link to="/sucursales">
                  <Button variant="outline" className="px-8 py-6 text-lg rounded-full border-2 border-[#1e40af] text-[#1e40af] hover:bg-[#1e40af] hover:text-white">
                    Ubicar sucursal
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1576201836106-db1758fd1c97?w=800&q=80" 
                alt="Laboratorio veterinario" 
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Promoción destacada */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-center gap-6 text-center">
            <div className="flex-1">
              <h2 className="text-4xl md:text-5xl font-bold mb-2">
                🎉 Descuentos por Inauguración
              </h2>
              <p className="text-xl md:text-2xl opacity-90 mb-4">
                Estamos celebrando nuestra apertura con promociones especiales
              </p>
              <p className="text-lg font-semibold">
                📞 Contáctanos para conocer nuestras ofertas
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Action Cards */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="group hover:shadow-xl transition-all duration-300 cursor-pointer border-2 hover:border-[#1e40af]">
              <CardContent className="p-8 text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-[#1e40af] rounded-full mb-4 group-hover:scale-110 transition-transform">
                  <Calendar className="h-10 w-10 text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#1e3a8a] mb-2">Cotiza y agenda</h3>
                <p className="text-gray-600 mb-4">tu cita ahora</p>
                <Link to="/agendar">
                  <Button className="bg-[#84cc16] hover:bg-[#65a30d] text-white rounded-full px-6">
                    Agendar <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </CardContent>
            </Card>

            <Card className="group hover:shadow-xl transition-all duration-300 cursor-pointer border-2 hover:border-[#84cc16]">
              <CardContent className="p-8 text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-[#84cc16] rounded-full mb-4 group-hover:scale-110 transition-transform">
                  <FileText className="h-10 w-10 text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#1e3a8a] mb-2">Consulta tus</h3>
                <p className="text-gray-600 mb-4">resultados</p>
                <Link to="/resultados">
                  <Button className="bg-[#84cc16] hover:bg-[#65a30d] text-white rounded-full px-6">
                    Ver resultados <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </CardContent>
            </Card>

            <Card className="group hover:shadow-xl transition-all duration-300 cursor-pointer border-2 hover:border-[#1e40af]">
              <CardContent className="p-8 text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-[#1e40af] rounded-full mb-4 group-hover:scale-110 transition-transform">
                  <CreditCard className="h-10 w-10 text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#1e3a8a] mb-2">Solicita tu</h3>
                <p className="text-gray-600 mb-4">factura</p>
                <Link to="/facturacion">
                  <Button className="bg-[#1e40af] hover:bg-[#1e3a8a] text-white rounded-full px-6">
                    Facturar <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Blog Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-3xl font-bold text-[#1e3a8a]">Interactúa con el blog de MoraBioVet</h2>
            <Link to="/blog">
              <Button variant="outline" className="text-[#1e40af] border-[#1e40af] hover:bg-[#1e40af] hover:text-white">
                Ver todos los artículos
              </Button>
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {blogPosts.slice(0, 3).map((post) => (
              <Card key={post.id} className="group hover:shadow-xl transition-all duration-300 cursor-pointer">
                <div className="relative overflow-hidden">
                  <img 
                    src={post.imagen} 
                    alt={post.titulo} 
                    className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-[#84cc16] text-white px-3 py-1 rounded-full text-sm font-semibold">
                      {post.categoria}
                    </span>
                  </div>
                </div>
                <CardContent className="p-6">
                  <p className="text-sm text-gray-500 mb-2">{post.fecha}</p>
                  <h3 className="text-lg font-bold text-[#1e3a8a] mb-2 group-hover:text-[#1e40af] transition-colors">
                    {post.titulo}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">{post.resumen}</p>
                  <Link to={`/blog/${post.id}`} className="text-[#84cc16] font-semibold hover:text-[#65a30d] inline-flex items-center">
                    Leer el artículo <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-3xl font-bold text-[#1e3a8a] mb-4">
                Estamos cerca de ti, ubica tu sucursal
              </h2>
              <p className="text-gray-600 mb-6">
                Encuentra la sucursal más cercana y agenda tu cita para toma de muestras.
              </p>
              <Link to="/sucursales">
                <Button className="bg-[#1e40af] hover:bg-[#1e3a8a] text-white px-8 py-6 text-lg rounded-full inline-flex items-center">
                  <MapPin className="mr-2 h-5 w-5" />
                  Ubica tu sucursal
                </Button>
              </Link>
            </div>
            <div className="relative h-96 bg-gray-200 rounded-2xl overflow-hidden shadow-xl">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3761.4876!2d-99.2690990!3d19.4139435!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x85d201501ffa57c1%3A0x2bd6272eebe80393!2sCenzontle%20137%2C%20Palo%20Solo%2C%2052778%20Naucalpan%20de%20Ju%C3%A1rez%2C%20M%C3%A9x.!5e0!3m2!1ses!2smx!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;